// ===== Frame sequence scroll-scrubber =====
const FRAME_COUNT = 264;
// TERRA_ASSET_BASE is injected by layout/theme.liquid via {{ 'mountain.webp' | asset_url }}
const FRAME_PATH = i => `${window.TERRA_ASSET_BASE}frame_${String(i).padStart(3, '0')}.webp`;
const NATIVE_W = 1280;
const NATIVE_H = 720;

const canvas = document.getElementById('watch-canvas');
const ctx = canvas ? canvas.getContext('2d') : null;
const loader = document.getElementById('loader');
const loaderFill = document.getElementById('loader-bar-fill');
const loaderPct = document.getElementById('loader-pct');

const images = new Array(FRAME_COUNT);
let loadedCount = 0;
let lastFrame = -1;

function setupCanvasResolution() {
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  canvas.width = NATIVE_W * dpr;
  canvas.height = NATIVE_H * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function drawFrame(index, force) {
  if (index === lastFrame && !force) return;
  const img = images[index];
  if (!img || !img.complete) return;
  lastFrame = index;
  ctx.clearRect(0, 0, NATIVE_W, NATIVE_H);
  ctx.drawImage(img, 0, 0, NATIVE_W, NATIVE_H);
}

const PRIORITY_FRAMES = Math.min(36, FRAME_COUNT); // enough footage buffered to start scrolling instantly; rest streams in behind it

function preloadFrames() {
  return new Promise(resolve => {
    let primed = false;
    let priorityLoaded = 0;
    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image();
      img.src = FRAME_PATH(i + 1);
      img.onload = img.onerror = () => {
        loadedCount++;
        if (i < PRIORITY_FRAMES) {
          priorityLoaded++;
          const pct = Math.round((priorityLoaded / PRIORITY_FRAMES) * 100);
          loaderFill.style.width = pct + '%';
          loaderPct.textContent = pct + '%';
        }
        if (i === 0) drawFrame(0);
        if (!primed && priorityLoaded >= PRIORITY_FRAMES) {
          primed = true;
          resolve();
        }
      };
      images[i] = img;
    }
  });
}

function initScrub() {
  gsap.registerPlugin(ScrollTrigger);

  const captions = gsap.utils.toArray('.scrub-caption');
  const scrollCue = document.getElementById('scroll-cue');
  const ranges = [
    [0, 0.95],
    [0.18, 0.42],
    [0.46, 0.72],
    [0.76, 1.0]
  ];

  const ZOOM_FROM = 1;
  const ZOOM_TO = 1.22;

  function update(progress) {
    const frameIndex = Math.min(
      FRAME_COUNT - 1,
      Math.round(progress * (FRAME_COUNT - 1))
    );
    drawFrame(frameIndex);

    const scale = ZOOM_FROM + (ZOOM_TO - ZOOM_FROM) * progress;
    canvas.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(3)})`;

    const growT = Math.min(1, progress / 0.16);
    const ellipseX = 50 + growT * 90;
    const ellipseY = 80 + growT * 70;
    const mask = `radial-gradient(ellipse ${ellipseX}% ${ellipseY}% at 50% 50%, #000 66%, transparent 100%)`;
    canvas.style.webkitMaskImage = mask;
    canvas.style.maskImage = mask;

    captions.forEach((el, i) => {
      const [start, end] = ranges[i];
      const active = progress >= start && progress <= end;
      el.classList.toggle('is-active', active);
    });

    scrollCue.classList.toggle('is-active', progress <= ranges[0][1]);
  }

  ScrollTrigger.create({
    trigger: '#scrub',
    start: 'top top',
    end: 'bottom bottom',
    scrub: true,
    onUpdate: self => update(self.progress)
  });

  update(0);
}

function initReveal() {
  const items = document.querySelectorAll('.section-inner');
  const io = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.2 }
  );
  items.forEach(el => io.observe(el));
}

async function boot() {
  if (canvas) {
    setupCanvasResolution();
    window.addEventListener('resize', () => {
      setupCanvasResolution();
      drawFrame(lastFrame, true);
    });

    await preloadFrames();
    initScrub();
  }

  if (loader) loader.classList.add('hidden');
  initReveal();
  initColorPick();
  initPromo();
  initReviewGallery();
  initPromoBar();
  initHeroCarousel();
  initTapToCart();
}

/* Plays a short selection animation on an edition card before following its link. */
function initColorPick() {
  const grid = document.querySelector('.colors-grid');
  if (!grid) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HOLD = reduced ? 0 : 560;

  grid.addEventListener('click', (e) => {
    const card = e.target.closest('.color-card');
    if (!card || !grid.contains(card)) return;

    // Let the browser handle new-tab / middle-click / modified clicks normally.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (card.classList.contains('is-picked')) return;

    e.preventDefault();

    grid.classList.add('is-picking');
    card.classList.add('is-picked');

    if (!reduced) {
      const r = card.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'pick-ripple';
      ripple.style.setProperty('--px', (e.clientX - r.left) + 'px');
      ripple.style.setProperty('--py', (e.clientY - r.top) + 'px');
      card.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    }

    setTimeout(() => { window.location.href = card.href; }, HOLD);
  });
}

/* Launch-offer popup. Opens a few seconds after landing, once per visitor per day. */
function initPromo() {
  const overlay = document.getElementById('promoOverlay');
  if (!overlay) return;

  const KEY = 'terra_promo_seen';
  const DAY = 24 * 60 * 60 * 1000;
  const DELAY = 3500;

  // Storage can throw in private mode — never let that block the page.
  let seenAt = 0;
  try { seenAt = Number(localStorage.getItem(KEY)) || 0; } catch (e) {}
  if (Date.now() - seenAt < DAY) return;

  const card = overlay.querySelector('.promo-card');
  let lastFocus = null;

  function open() {
    lastFocus = document.activeElement;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    document.getElementById('promoClose').focus();
    try { localStorage.setItem(KEY, String(Date.now())); } catch (e) {}
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    const done = () => { overlay.hidden = true; };
    // Fall back to a timer if the transition never fires (reduced motion).
    let settled = false;
    overlay.addEventListener('transitionend', function once() {
      if (settled) return;
      settled = true;
      overlay.removeEventListener('transitionend', once);
      done();
    });
    setTimeout(() => { if (!settled) { settled = true; done(); } }, 400);
    if (lastFocus) lastFocus.focus();
  }

  document.getElementById('promoClose').addEventListener('click', close);
  document.getElementById('promoCta').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hidden) close();
  });
  // Keep tabbing inside the dialog while it is open.
  card.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const f = card.querySelectorAll('button, a[href]');
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  setTimeout(open, DELAY);
}

/* Thumbnails inside a review card swap that card's main photo. */
function initReviewGallery() {
  const grid = document.querySelector('.reviews-grid');
  if (!grid) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  grid.addEventListener('click', (e) => {
    const thumb = e.target.closest('.review-thumb');
    if (!thumb || thumb.classList.contains('is-active')) return;

    const card = thumb.closest('.review-card');
    const main = card && card.querySelector('.review-photo img');
    if (!main) return;

    card.querySelectorAll('.review-thumb').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');

    const src = thumb.dataset.src;
    if (reduced) { main.src = src; return; }

    // The thumbnail already decoded this file, so the fade never lands blank.
    main.classList.add('is-swapping');
    setTimeout(() => {
      main.src = src;
      // A timer, not requestAnimationFrame: rAF is frozen while the tab is
      // hidden, which would strand the photo at opacity 0.
      setTimeout(() => main.classList.remove('is-swapping'), 20);
    }, 200);
  });
}

/* ===== Offer deadline =====
   A single real end date, shared by every visitor. Deliberately NOT a
   per-visit timer that restarts: a countdown that never actually expires
   is a misleading commercial practice under EU consumer rules.
   Format: 'YYYY-MM-DDTHH:MM:SS' in the shop's local time.        */
const OFFER_ENDS = '2026-08-17T23:59:59';

/* Counts down to OFFER_ENDS, then removes the bar for good. */
function initPromoBar() {
  const bar = document.getElementById('promoBar');
  // Two copies of the message exist for the phone marquee, so both clocks tick.
  const outs = bar ? bar.querySelectorAll('.promo-bar-time') : [];
  if (!bar || !outs.length) return;

  const end = new Date(OFFER_ENDS).getTime();
  if (isNaN(end)) return;                     // bad date → never show the bar

  const pad = (n) => String(n).padStart(2, '0');

  function tick() {
    const left = end - Date.now();
    if (left <= 0) {
      clearInterval(timer);
      bar.hidden = true;
      document.body.classList.remove('has-promo-bar');
      return;
    }
    const d = Math.floor(left / 86400000);
    const h = Math.floor(left / 3600000) % 24;
    const m = Math.floor(left / 60000) % 60;
    const s = Math.floor(left / 1000) % 60;
    const text = d > 0
      ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`
      : `${pad(h)}:${pad(m)}:${pad(s)}`;
    outs.forEach((el) => { el.textContent = text; });
  }

  if (end - Date.now() <= 0) return;          // already over: stay hidden

  tick();
  bar.hidden = false;
  document.body.classList.add('has-promo-bar');
  // Reserve exactly the height the bar actually renders at.
  document.body.style.setProperty('--promo-bar-h', bar.offsetHeight + 'px');
  const timer = setInterval(tick, 1000);

  window.addEventListener('resize', () => {
    if (!bar.hidden) {
      document.body.style.setProperty('--promo-bar-h', bar.offsetHeight + 'px');
    }
  });
}

/* Swipeable edition carousel. Dragging, dots and chips all drive the same
   index, and the buy button always points at whichever watch is on screen. */
function initHeroCarousel() {
  const track = document.getElementById('heroTrack');
  const carousel = document.getElementById('heroCarousel');
  if (!track || !carousel) return;

  const slides = [...track.querySelectorAll('.hero-slide')];
  const dots = [...document.querySelectorAll('.hero-dot')];
  const cards = [...document.querySelectorAll('.pick-card')];
  if (!slides.length) return;

  let index = 0;

  function render(animate) {
    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translateX(${-index * 100}%)`;
    dots.forEach((d, n) => d.classList.toggle('is-active', n === index));
    cards.forEach((c, n) => c.classList.toggle('is-active', n === index));
  }

  function go(n) {
    index = Math.max(0, Math.min(slides.length - 1, n));
    render(true);
  }

  dots.forEach((d) => d.addEventListener('click', () => go(+d.dataset.index)));
  // The tap-to-cart picker below asks the carousel to show the colour it just added.
  window.terraShowEdition = go;

  // Drag / swipe. Pointer events cover finger, trackpad and mouse alike.
  let startX = 0, dx = 0, dragging = false;
  const width = () => carousel.getBoundingClientRect().width || 1;

  carousel.addEventListener('pointerdown', (e) => {
    dragging = true; startX = e.clientX; dx = 0;
    track.style.transition = 'none';
    carousel.setPointerCapture(e.pointerId);
  });
  carousel.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    dx = e.clientX - startX;
    track.style.transform = `translateX(${-index * 100 + (dx / width()) * 100}%)`;
  });
  function end() {
    if (!dragging) return;
    dragging = false;
    // A quarter of the width is enough intent to change edition.
    if (Math.abs(dx) > width() * 0.25) go(index + (dx < 0 ? 1 : -1));
    else render(true);
    dx = 0;
  }
  carousel.addEventListener('pointerup', end);
  carousel.addEventListener('pointercancel', end);
  carousel.addEventListener('pointerleave', end);

  // Dragging over an image must not trigger the browser's own image drag.
  carousel.querySelectorAll('img').forEach((im) => {
    im.addEventListener('dragstart', (e) => e.preventDefault());
  });

  carousel.setAttribute('tabindex', '0');
  carousel.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') go(index + 1);
    if (e.key === 'ArrowLeft') go(index - 1);
  });

  render(false);
}

/* Tap a colour, watch it fly into the cart, tap again for a second one.
   The line items are created here at submit time and only for colours that
   were actually tapped — an untouched colour has no input at all, so it
   cannot slip into the cart the way a hidden zero-quantity field could. */
function initTapToCart() {
  const grid = document.getElementById('pickGrid');
  const form = document.getElementById('heroForm');
  const bin = document.getElementById('cartItems');
  const buy = document.getElementById('heroBuy');
  const cart = document.getElementById('pickCart');
  const text = document.getElementById('pickText');
  if (!grid || !form || !buy) return;

  const cards = [...grid.querySelectorAll('.pick-card')];
  const counts = new Map();
  const slotOf = (card) => card.closest('.pick-slot') || card;

  function total() {
    let n = 0;
    counts.forEach((v) => { n += v; });
    return n;
  }

  function refresh() {
    cards.forEach((c) => {
      const n = counts.get(c.dataset.variant) || 0;
      const slot = slotOf(c);
      const badge = slot.querySelector('.pick-badge');
      const minus = slot.querySelector('.pick-minus');
      c.classList.toggle('is-chosen', n > 0);
      if (badge) {
        badge.textContent = n;
        badge.hidden = n === 0;
      }
      // Nothing to take away until there is something there.
      if (minus) minus.hidden = n === 0;
    });
    const n = total();
    // Kept fully coloured and pulsing even at zero: a greyed-out button reads
    // as "sold out" to someone who has been on the page for three seconds.
    // It simply refuses to submit until a colour has been tapped.
    buy.setAttribute('aria-disabled', n === 0 ? 'true' : 'false');
    if (cart) cart.classList.toggle('has-items', n > 0);
    if (!text) return;
    if (n === 0) text.textContent = 'Aún no has elegido ninguno';
    else if (n === 1) text.textContent = '1 reloj listo · toca otro color para sumar';
    else text.textContent = n + ' relojes · la oferta 2×49,95 € se aplica al pagar';
  }

  // The watch itself leaves the carousel and lands on the cart icon.
  function fly(card) {
    if (!cart || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const shots = document.querySelectorAll('.hero-slide img');
    const slide = shots[+card.dataset.index] || shots[0];
    const from = (slide || card).getBoundingClientRect();
    const to = cart.getBoundingClientRect();

    const ghost = document.createElement(slide ? 'img' : 'span');
    if (slide) ghost.src = slide.currentSrc || slide.src;
    ghost.className = 'fly-ghost';
    ghost.style.left = from.left + 'px';
    ghost.style.top = from.top + 'px';
    ghost.style.width = from.width + 'px';
    ghost.style.height = from.height + 'px';
    if (!slide) ghost.style.background = getComputedStyle(card).getPropertyValue('--swatch');
    document.body.appendChild(ghost);

    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    requestAnimationFrame(() => {
      ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.08)';
      ghost.style.opacity = '0.15';
    });
    setTimeout(() => ghost.remove(), 750);
    cart.classList.remove('is-bumping');
    void cart.offsetWidth;
    cart.classList.add('is-bumping');
  }

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      if (card.disabled) return;
      // Show the colour first so the thing flying to the cart is the right watch.
      if (typeof window.terraShowEdition === 'function') {
        window.terraShowEdition(+card.dataset.index);
      }
      counts.set(card.dataset.variant, (counts.get(card.dataset.variant) || 0) + 1);
      refresh();
      setTimeout(() => fly(card), 60);
    });

    // The small minus by the badge undoes a tap too many.
    const minus = slotOf(card).querySelector('.pick-minus');
    if (minus) {
      minus.addEventListener('click', (e) => {
        e.stopPropagation();
        const n = counts.get(card.dataset.variant) || 0;
        if (n <= 1) counts.delete(card.dataset.variant);
        else counts.set(card.dataset.variant, n - 1);
        refresh();
      });
    }
  });

  form.addEventListener('submit', (e) => {
    if (total() === 0) {
      // Say what is missing instead of going quiet: the tiles shake and the
      // line under them turns into an instruction.
      e.preventDefault();
      grid.classList.remove('is-nudging');
      void grid.offsetWidth;
      grid.classList.add('is-nudging');
      if (text) {
        text.textContent = 'Toca un color para elegir tu reloj';
        text.classList.add('is-warning');
        setTimeout(() => { text.classList.remove('is-warning'); refresh(); }, 2200);
      }
      grid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (!bin) return;
    bin.innerHTML = '';
    let line = 0;
    counts.forEach((qty, id) => {
      if (qty < 1) return;
      bin.insertAdjacentHTML('beforeend',
        '<input type="hidden" name="items[' + line + '][id]" value="' + id + '">' +
        '<input type="hidden" name="items[' + line + '][quantity]" value="' + qty + '">');
      line += 1;
    });
  });

  refresh();
}

boot();
